/**
 * Audit trail — Specification §15.
 *
 * What appears here is decided entirely by the server (C-05): an Admin sees
 * every action, an Office Manager sees their own office's, a Regular User sees
 * only their own. The filters below can narrow that set, never widen it.
 *
 * Entries are immutable and there is no delete control, because there is no
 * delete: the application's database role holds INSERT and SELECT on
 * `audit_logs` and nothing else. An audit trail an operator can edit is not
 * one.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../lib/api';
import { Empty, ErrorState, Panel, TableSkeleton, formatTimestamp } from '../components/ui';

interface AuditEntry {
  readonly occurredAt: string;
  readonly action: string;
  readonly summary: string | null;
  readonly entityType: string;
  readonly entityId: string | null;
  readonly confirmationId: string | null;
  readonly actorDisplayName: string | null;
  readonly actorRoleKey: string | null;
  readonly ipAddress: string | null;
  readonly changes: unknown;
}

/**
 * Actions grouped by consequence rather than by module.
 *
 * Somebody scanning a trail is looking for "who changed permissions" or "who
 * removed something", not for "which service emitted this". The colour is a
 * second channel on top of the action name, never the only one.
 */
function toneFor(action: string): string {
  if (/(delete|cancel|revoke|disable|archive|fail|lock)/i.test(action)) return 'var(--color-danger)';
  if (/(role|permission|password|reset|login)/i.test(action)) return 'var(--color-warning)';
  if (/(create|send|reply|resolve|close)/i.test(action)) return 'var(--color-accent)';
  return 'var(--color-ink-muted)';
}

/**
 * The before/after payload.
 *
 * Collapsed by default: most rows are read for "what happened and when", and
 * expanding every diff inline would bury that under JSON.
 */
function Changes({ value }: { value: unknown }) {
  const [open, setOpen] = useState(false);
  if (value === null || value === undefined) {
    return <span style={{ color: 'var(--color-ink-subtle)' }}>—</span>;
  }

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="interactive text-xs font-medium"
        style={{ color: 'var(--color-accent)' }}
      >
        {open ? 'Hide detail' : 'Show detail'}
      </button>
      {open && (
        <pre
          className="animate-rise mt-1 max-h-48 overflow-auto px-2 py-1.5 text-xs"
          style={{
            background: 'var(--color-canvas)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-ink-muted)',
          }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function AuditLog() {
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [limit, setLimit] = useState(50);

  const actions = useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: () => api.get<string[]>('/api/v1/audit-logs/actions'),
  });

  const params = new URLSearchParams();
  if (action !== '') params.set('action', action);
  if (from !== '') params.set('from', from);
  // The upper bound is exclusive on the server, so a whole chosen day is
  // included by advancing to the next midnight rather than by asking the user
  // to enter tomorrow's date.
  if (to !== '') params.set('to', new Date(`${to}T00:00:00Z`).toISOString());
  params.set('limit', String(limit));

  const entries = useQuery({
    queryKey: ['audit', 'entries', params.toString()],
    queryFn: () => api.get<AuditEntry[]>(`/api/v1/audit-logs?${params.toString()}`),
  });

  const fieldStyle = {
    borderRadius: 'var(--radius-sm)',
    borderColor: 'var(--color-rule-strong)',
    background: 'var(--color-surface)',
  } as const;

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">Audit trail</h1>
        <p className="mt-0.5 text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
          Every recorded action, in the order it happened. Entries cannot be edited or removed.
        </p>
      </div>

      <Panel
        title="Filters"
        actions={
          <span className="text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
            {entries.isSuccess ? `${entries.data.length} shown` : ''}
          </span>
        }
      >
        <div className="grid gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm">
            <span className="mb-1 block font-medium">Action</span>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="interactive w-full border px-2.5 py-2"
              style={fieldStyle}
            >
              <option value="">All actions</option>
              {(actions.data ?? []).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="interactive w-full border px-2.5 py-2"
              style={fieldStyle}
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="interactive w-full border px-2.5 py-2"
              style={fieldStyle}
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block font-medium">Show</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="interactive w-full border px-2.5 py-2"
              style={fieldStyle}
            >
              {[25, 50, 100, 200].map((value) => (
                <option key={value} value={value}>
                  {value} entries
                </option>
              ))}
            </select>
          </label>
        </div>
      </Panel>

      <Panel>
        {entries.isPending && <TableSkeleton rows={8} columns={5} />}
        {entries.isError && (
          <ErrorState
            message="Could not load the audit trail."
            onRetry={() => void entries.refetch()}
          />
        )}
        {entries.isSuccess && entries.data.length === 0 && (
          <Empty
            title="Nothing matches those filters"
            detail="Widen the period, or clear the action filter."
          />
        )}
        {entries.isSuccess && entries.data.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead className="sticky-head">
                <tr
                  className="border-b text-xs font-medium uppercase tracking-wider"
                  style={{ borderColor: 'var(--color-rule)', color: 'var(--color-ink-subtle)' }}
                >
                  <th scope="col" className="px-4 py-2">When</th>
                  <th scope="col" className="px-4 py-2">Action</th>
                  <th scope="col" className="px-4 py-2">Who</th>
                  <th scope="col" className="px-4 py-2">What</th>
                  <th scope="col" className="px-4 py-2">Detail</th>
                </tr>
              </thead>
              <tbody className="stagger">
                {entries.data.map((entry, index) => (
                  <tr
                    // The trail has no surrogate key exposed; time plus action
                    // plus position is stable for a rendered page.
                    key={`${entry.occurredAt}-${entry.action}-${index}`}
                    className="row-hover border-b align-top last:border-b-0"
                    style={{ borderColor: 'var(--color-rule)' }}
                  >
                    <td
                      className="tabular px-4 py-2 text-xs whitespace-nowrap"
                      style={{ color: 'var(--color-ink-muted)' }}
                    >
                      {formatTimestamp(entry.occurredAt)}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className="reference text-xs font-medium"
                        style={{ color: toneFor(entry.action) }}
                      >
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <div>{entry.actorDisplayName ?? 'System'}</div>
                      <div className="text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                        {entry.actorRoleKey?.replace('_', ' ').toLowerCase() ?? 'automated'}
                        {entry.ipAddress !== null && ` · ${entry.ipAddress}`}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <div>{entry.summary ?? entry.entityType}</div>
                      {entry.confirmationId !== null && (
                        <Link
                          to={`/confirmations/${entry.confirmationId}`}
                          className="interactive text-xs"
                          style={{ color: 'var(--color-accent)' }}
                        >
                          Open confirmation
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Changes value={entry.changes} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
